import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { benchmarkService } from '../../services/benchmarkService';
import { ComparisonView } from '../../components/benchmark/ComparisonView';
import { StabilityScoreCard } from '../../components/benchmark/StabilityScoreCard';
import type { BenchmarkRun, RunComparison, StabilityScore } from '../../types/benchmark';
import { PageContainer } from '../../components/layout';

export const ComparaisonPage = () => {
  const [runs, setRuns] = useState<BenchmarkRun[]>([]);
  const [referenceRunId, setReferenceRunId] = useState<string>('');
  const [compareRunId, setCompareRunId] = useState<string>('');
  const [comparison, setComparison] = useState<RunComparison | null>(null);
  const [stability, setStability] = useState<StabilityScore | null>(null);
  const [loading, setLoading] = useState(true);
  const [comparing, setComparing] = useState(false);

  useEffect(() => {
    loadRuns();
  }, []);

  useEffect(() => {
    if (referenceRunId && compareRunId && referenceRunId !== compareRunId) {
      loadComparison();
      loadStability();
    } else {
      setComparison(null);
      setStability(null);
    }
  }, [referenceRunId, compareRunId]);

  const loadRuns = async () => {
    try {
      setLoading(true);
      const data = await benchmarkService.getRuns();
      // Only show completed runs
      const completedRuns = data.filter(r => r.status === 'completed');
      setRuns(completedRuns);

      // Pre-select last two runs if available
      if (completedRuns.length >= 2) {
        setReferenceRunId(completedRuns[0].id);
        setCompareRunId(completedRuns[1].id);
      } else if (completedRuns.length === 1) {
        setReferenceRunId(completedRuns[0].id);
      }
    } catch (err) {
      toast.error('Erreur lors du chargement des runs');
      console.error('Failed to load runs:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadComparison = async () => {
    if (!referenceRunId || !compareRunId) return;

    try {
      setComparing(true);
      const data = await benchmarkService.compareRuns(referenceRunId, compareRunId);
      setComparison(data);
    } catch (err) {
      toast.error('Erreur lors de la comparaison');
      console.error('Failed to load comparison:', err);
      setComparison(null);
    } finally {
      setComparing(false);
    }
  };

  const loadStability = async () => {
    if (!referenceRunId || !compareRunId) return;

    try {
      const data = await benchmarkService.getStability(referenceRunId, compareRunId);
      setStability(data);
    } catch (err) {
      console.error('Failed to load stability score:', err);
      setStability(null);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-500">Chargement...</div>
      </div>
    );
  }

  if (runs.length === 0) {
    return (
      <div className="bg-white border rounded-lg p-8 text-center">
        <h2 className="text-xl font-semibold mb-2">Aucune run disponible</h2>
        <p className="text-gray-600 mb-4">
          Vous devez avoir au moins une run complétée pour utiliser la comparaison.
        </p>
        <Link
          to="/benchmark/analyse"
          className="inline-block px-4 py-2 bg-primary text-white rounded hover:bg-primary-600"
        >
          Aller à Analyse
        </Link>
      </div>
    );
  }

  return (
    <PageContainer>
      <div className="bg-white border rounded-lg p-6">
        <h1 className="text-2xl font-bold mb-6">Benchmark - Comparaison</h1>

        <div className="grid grid-cols-2 gap-6 mb-6">
          {/* Reference Run Selector */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Run de référence
            </label>
            <select
              value={referenceRunId}
              onChange={(e) => setReferenceRunId(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Sélectionner une run</option>
              {runs.map((run) => (
                <option key={run.id} value={run.id}>
                  [{run.id.substring(0, 8)}] {run.note ? `${run.note} - ` : ''}{run.prompt_version || 'N/A'} (F1: {run.avg_f1 ? Number(run.avg_f1).toFixed(3) : 'N/A'})
                </option>
              ))}
            </select>
            {referenceRunId && (() => {
              const run = runs.find(r => r.id === referenceRunId);
              if (!run) return null;
              return (
                <p className="text-xs text-gray-500 mt-1">
                  Schéma: v{run.schema?.version || '-'} | Prompt: v{run.prompt?.version || '-'} | LLM: v{run.llm_config?.version || '-'} | OCR: v{run.ocr_config?.version || '-'}
                </p>
              );
            })()}
          </div>

          {/* Compare Run Selector */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Run à comparer
            </label>
            <select
              value={compareRunId}
              onChange={(e) => setCompareRunId(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Sélectionner une run</option>
              {runs.map((run) => (
                <option key={run.id} value={run.id}>
                  [{run.id.substring(0, 8)}] {run.note ? `${run.note} - ` : ''}{run.prompt_version || 'N/A'} (F1: {run.avg_f1 ? Number(run.avg_f1).toFixed(3) : 'N/A'})
                </option>
              ))}
            </select>
            {compareRunId && (() => {
              const run = runs.find(r => r.id === compareRunId);
              if (!run) return null;
              return (
                <p className="text-xs text-gray-500 mt-1">
                  Schéma: v{run.schema?.version || '-'} | Prompt: v{run.prompt?.version || '-'} | LLM: v{run.llm_config?.version || '-'} | OCR: v{run.ocr_config?.version || '-'}
                </p>
              );
            })()}
          </div>
        </div>

        {!referenceRunId || !compareRunId ? (
          <div className="bg-gray-50 border border-gray-200 rounded p-4 text-center text-gray-600">
            Sélectionnez deux runs pour voir la comparaison
          </div>
        ) : referenceRunId === compareRunId ? (
          <div className="bg-yellow-50 border border-yellow-200 rounded p-4 text-center text-yellow-800">
            ⚠️ Veuillez sélectionner deux runs différentes
          </div>
        ) : comparing ? (
          <div className="bg-gray-50 border border-gray-200 rounded p-4 text-center text-gray-600">
            Chargement de la comparaison...
          </div>
        ) : null}
      </div>

      {comparison && <ComparisonView comparison={comparison} />}

      {stability && <StabilityScoreCard stability={stability} />}
    </PageContainer>
  );
};
